import { useTranslation } from 'react-i18next';
import { useArtifact } from '@/api/queries/useArtifacts';
import { notFoundOrUnknownKey } from '@/api/errors';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BackButton } from '@/components/back-button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  EntityDetailTitle,
  Skeleton,
  Page,
  PageContent,
  formatDate,
  formatFileSize,
  renderFallback
} from '@bliks-io/ui';

interface ArtifactDetailPageProps {
  artifactId: string;
}

export function ArtifactDetailPage({ artifactId }: ArtifactDetailPageProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { data: artifact, isPending, error } = useArtifact(artifactId);

  useDocumentTitle('pageTitles.artifactDetail', { name: artifact?.name || 'Loading...' });

  if (isPending) {
    return (
      <Page>
        <PageContent>
          <Skeleton className="h-8 w-64 mb-8" />
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  if (error || !artifact) {
    return (
      <Page>
        <PageContent>
          <div className="flex items-center gap-4 mb-8">
            <BackButton to="/$slug/organization/artifacts" params={{ slug }} />
          </div>
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">
                {t(notFoundOrUnknownKey(error, 'organization.artifacts.notFound'))}
              </p>
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/$slug/organization/artifacts" params={{ slug }} />
          <div>
            <EntityDetailTitle name={artifact.name} deletedAt={artifact.deletedAt} />
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t('organization.artifacts.detailDescription')}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('organization.artifacts.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Name')}</label>
                <p className="mt-1 text-base">{artifact.name}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Description')}</label>
                <p className="mt-1 text-base">{renderFallback(artifact.description)}</p>
              </div>

              {artifact.type && (
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Type')}</label>
                  <div className="mt-1">
                    <Badge variant="secondary">{artifact.type}</Badge>
                  </div>
                </div>
              )}

              {artifact.size && (
                <div>
                  <label className="text-sm font-medium text-gray-8">
                    {t('organization.artifacts.size')}
                  </label>
                  <p className="mt-1 text-base">{formatFileSize(artifact.size)}</p>
                </div>
              )}

              {artifact.fileUrl && (
                <div>
                  <label className="text-sm font-medium text-gray-8">
                    {t('organization.artifacts.fileUrl')}
                  </label>
                  <p className="mt-1 text-base">
                    <a
                      href={artifact.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline break-all"
                    >
                      {artifact.fileUrl}
                    </a>
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('organization.artifacts.createdBy')}
                </label>
                <p className="mt-1 text-base">{renderFallback(artifact.createdByName)}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Created')}</label>
                  <p className="mt-1 text-base">{formatDate(artifact.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Updated')}</label>
                  <p className="mt-1 text-base">{formatDate(artifact.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </Page>
  );
}
